import { useCallback, useEffect, useState } from 'react';
import {
  adminGetTokenPackages,
  adminSaveTokenPackage,
  FALLBACK_PACKAGES,
  normalizePackage,
} from '../../services/apiService';
import { logActivity } from '../../services/adminMockData';

// Token packages (Starter, Growth, Unlimited, …) — the table token_packages.
// What is set here is what retailers see on "Tokens kopen" and pay at checkout.

const toCents = (v) => Math.round(parseFloat(String(v).replace(',', '.')) * 100) || 0;
const fromCents = (c) => (Number(c || 0) / 100).toFixed(2).replace('.', ',');

const EMPTY = {
  packageId: null, code: '', name: '', type: 'tokens', tokens: 10,
  durationMonths: 1, price: '0,00', vatRate: 21, description: '', active: true, sortOrder: 10,
};

function toForm(p) {
  return {
    packageId: p.packageId || null,
    code: p.code || p.id || '',
    name: p.name || '',
    type: p.type,
    tokens: typeof p.tokens === 'number' ? p.tokens : 0,
    durationMonths: p.durationMonths || 1,
    price: fromCents(p.priceExCents),
    vatRate: p.vatRate ?? 21,
    description: p.description || '',
    active: p.active !== false,
    sortOrder: p.sortOrder ?? 0,
  };
}

export default function AdminPackagesPage() {
  const [packages, setPackages] = useState([]);
  const [live, setLive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);   // form object | null
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPackages(await adminGetTokenPackages());
      setLive(true);
    } catch (err) {
      setPackages(FALLBACK_PACKAGES.map(normalizePackage));
      setLive(false);
      if (err?.status && err.status !== 404) setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError('');
    if (!edit.name.trim()) { setError('Naam is verplicht'); return; }
    if (!/^[a-z0-9-]{2,30}$/.test(edit.code)) {
      setError('Code: 2–30 tekens, kleine letters, cijfers of -'); return;
    }
    if (edit.type === 'tokens' && !(Number(edit.tokens) > 0)) {
      setError('Aantal tokens moet groter dan 0 zijn'); return;
    }
    setSaving(true);
    try {
      await adminSaveTokenPackage({
        ...edit,
        priceExCents: toCents(edit.price),
      });
      logActivity(edit.packageId ? 'Pakket bijgewerkt' : 'Pakket aangemaakt', { pakket: edit.name });
      setToast(`${edit.name} opgeslagen`);
      setTimeout(() => setToast(''), 2500);
      setEdit(null);
      await load();
    } catch (err) {
      setError(err?.status === 409 ? 'Deze code bestaat al' : err?.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setEdit((f) => ({ ...f, [k]: v }));
  const vatPreview = edit ? Math.round(toCents(edit.price) * Number(edit.vatRate || 0) / 100) : 0;

  return (
    <div>
      {toast && <div className="admin__toast admin__toast--success">{toast}</div>}

      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Pakketten</h1>
          <p className="admin-page-sub">
            Tokenpakketten en het Unlimited-abonnement — prijzen zoals de winkelier ze ziet
          </p>
        </div>
        {live && (
          <button className="admin-btn admin-btn--orange" onClick={() => { setError(''); setEdit({ ...EMPTY }); }}>
            + Nieuw pakket
          </button>
        )}
      </header>

      {!live && !loading && (
        <div className="admin-card" style={{ background: '#FFFBEB', borderColor: '#FCD34D', color: '#92400E' }}>
          De backend heeft de tabel <strong>token_packages</strong> nog niet (zie
          BACKEND_CHANGES_0.3.md, sectie J). Hieronder de standaardpakketten; bewerken
          kan zodra de migratie is uitgevoerd.
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Volgorde</th><th>Pakket</th><th>Code</th><th>Type</th><th>Inhoud</th>
              <th>Prijs ex BTW</th><th>Incl. BTW</th><th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="admin-empty">Laden…</td></tr>}
            {!loading && packages.map((p) => (
              <tr key={p.packageId || p.id}>
                <td>{p.sortOrder}</td>
                <td><strong>{p.name}</strong><div style={{ fontSize: 12, color: '#6B7280' }}>{p.description}</div></td>
                <td><span className="admin-mono">{p.id}</span></td>
                <td>{p.type === 'subscription' ? 'Abonnement' : 'Tokens'}</td>
                <td>{p.type === 'subscription' ? `Onbeperkt, per ${p.durationMonths || 1} mnd` : `${p.tokens} tokens`}</td>
                <td>€{p.priceEx}</td>
                <td>€{p.priceIncl}</td>
                <td>
                  <span className={`admin-badge admin-badge--${p.active ? 'actief' : 'inactief'}`}>
                    {p.active ? 'actief' : 'inactief'}
                  </span>
                </td>
                <td>
                  {live && (
                    <button className="admin-btn admin-btn--small admin-btn--ghost"
                      onClick={() => { setError(''); setEdit(toForm(p)); }}>
                      Bewerken
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="admin-modal" role="dialog" onClick={() => !saving && setEdit(null)}>
          <form className="admin-modal__card" onClick={(e) => e.stopPropagation()} onSubmit={save} style={{ maxWidth: 560 }}>
            <h3 className="admin-modal__title">{edit.packageId ? `Bewerk ${edit.name}` : 'Nieuw pakket'}</h3>

            <div className="admin-form-row">
              <div className="admin-field">
                <span className="admin-field__label">Naam</span>
                <input className="admin-input" value={edit.name} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div className="admin-field">
                <span className="admin-field__label">Code</span>
                <input className="admin-input" value={edit.code} disabled={!!edit.packageId}
                  onChange={(e) => set('code', e.target.value.toLowerCase().replace(/\s+/g, '-'))} />
                <span className="admin-field__hint">Vast na aanmaken (wordt gebruikt in links en facturen)</span>
              </div>
            </div>

            <div className="admin-form-row">
              <div className="admin-field">
                <span className="admin-field__label">Type</span>
                <select className="admin-select" value={edit.type} disabled={!!edit.packageId}
                  onChange={(e) => set('type', e.target.value)}>
                  <option value="tokens">Tokens (eenmalig)</option>
                  <option value="subscription">Unlimited-abonnement</option>
                </select>
              </div>
              {edit.type === 'tokens' ? (
                <div className="admin-field">
                  <span className="admin-field__label">Aantal tokens</span>
                  <input className="admin-input" type="number" min={1} value={edit.tokens}
                    onChange={(e) => set('tokens', e.target.value)} />
                </div>
              ) : (
                <div className="admin-field">
                  <span className="admin-field__label">Looptijd per betaling (maanden)</span>
                  <input className="admin-input" type="number" min={1} value={edit.durationMonths}
                    onChange={(e) => set('durationMonths', e.target.value)} />
                </div>
              )}
            </div>

            <div className="admin-form-row">
              <div className="admin-field">
                <span className="admin-field__label">Prijs ex BTW (€)</span>
                <input className="admin-input" inputMode="decimal" value={edit.price}
                  onChange={(e) => set('price', e.target.value)} />
                <span className="admin-field__hint">
                  BTW €{fromCents(vatPreview)} · incl. €{fromCents(toCents(edit.price) + vatPreview)}
                </span>
              </div>
              <div className="admin-field">
                <span className="admin-field__label">BTW %</span>
                <input className="admin-input" type="number" min={0} max={100} value={edit.vatRate}
                  onChange={(e) => set('vatRate', e.target.value)} />
              </div>
            </div>

            <div className="admin-field">
              <span className="admin-field__label">Omschrijving (op factuur)</span>
              <input className="admin-input" value={edit.description} onChange={(e) => set('description', e.target.value)} />
            </div>

            <div className="admin-form-row">
              <div className="admin-field">
                <span className="admin-field__label">Volgorde</span>
                <input className="admin-input" type="number" value={edit.sortOrder}
                  onChange={(e) => set('sortOrder', e.target.value)} />
              </div>
              <label className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingTop: 22 }}>
                <input type="checkbox" checked={edit.active} onChange={(e) => set('active', e.target.checked)} />
                Actief (zichtbaar voor winkeliers)
              </label>
            </div>

            {error && <div style={{ color: '#991B1B', fontSize: 13, fontWeight: 600 }}>{error}</div>}

            <div className="admin-modal__actions">
              <button type="button" className="admin-btn admin-btn--ghost" disabled={saving} onClick={() => setEdit(null)}>
                Annuleren
              </button>
              <button type="submit" className="admin-btn admin-btn--orange" disabled={saving}>
                {saving ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
